// TableContainer.js
import React, { Fragment, useEffect, useMemo } from "react"
import { useTable, useSortBy, useFilters, usePagination } from "react-table"
import { useExportData } from "react-table-plugins"
import { DefaultColumnFilter, Filter } from "./Filters"
import { Input } from "reactstrap"
import { Form, Table, Button, Row, Col } from 'react-bootstrap'
import Papa from "papaparse"
import PropTypes from "prop-types"

const TableContainer = (props) => {
  const columns = props.columns;

  const getExportFileBlob = ({ columns, data, fileType, fileName }) => {
    const headerNames = columns.map(col => col.exportValue);

    if (fileType === "csv") {
      const csvString = Papa.unparse({ fields: headerNames, data });

      return new Blob([csvString], { type: "text/csv" });
    }

    return false
  }

  const data = props.data;
  const persistKey = props.persistKey;

  const initialState = useMemo(() => {
  const baseState = { pageIndex: 0, pageSize: 10 };

  if (!persistKey || !globalThis.localStorage) {
    return baseState;
  }

  try {
    const stored = globalThis.localStorage.getItem(persistKey);
    const parsed = stored ? JSON.parse(stored) : null;
    return parsed ? { ...baseState, ...parsed } : baseState;
    } catch (error) {
      console.log("Error accessing localStorage", error);
      return baseState;
    }
  }, [persistKey]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    // below new props related to 'usePagination' hook
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize, sortBy },
    exportData
  } = useTable({
      columns,
      data,
      defaultColumn: { Filter: DefaultColumnFilter },
      initialState,
      getExportFileBlob,
      autoResetPage: false
    },
    useFilters,
    useSortBy,
    usePagination,
    useExportData
  )

  const totalPages = pageOptions.length
  const currentPage = totalPages === 0 ? 0 : Math.min(pageIndex + 1, totalPages)

  useEffect(() => {
    if (pageCount > 0 && pageIndex >= pageCount) {
      gotoPage(pageCount - 1)
    }
  }, [gotoPage, pageCount, pageIndex])

  useEffect(() => {
    if (!persistKey || !globalThis.localStorage) {
      return
    }
    if (pageCount === 0 || pageIndex >= pageCount) {
      return
    }
    try {
      globalThis.localStorage.setItem(
        persistKey,
        JSON.stringify({ pageIndex, pageSize, sortBy })
      )
    } catch (error) {
      console.log("Could not persist table state", error)
    }
  }, [persistKey, pageCount, pageIndex, pageSize, sortBy])

  const generateSortingIndicator = column => {
    return column.isSorted ? (column.isSortedDesc ? " ▼" : " ▲") : ""
  }

  const onChangePageSize = event => {
    setPageSize(Number(event.target.value))
  }

  const onChangePageNumber = event => {
    if (pageCount === 0) {
      return
    }

    const requestedPage = event.target.value ? Number(event.target.value) : 1
    if (!Number.isFinite(requestedPage)) {
      return
    }

    const page = Math.min(Math.max(requestedPage - 1, 0), pageCount - 1)
    gotoPage(page)
  }

  return (
    // If you're curious what props we get as a result of calling our getter functions (getTableProps(), getRowProps())
    // Feel free to use console.log()  This will help you better understand how react table works underhood.
      <Fragment>
      <Table bordered  {...getTableProps()}>
        {/* Bootstrap table uses thead, tr, th, tbody rather than new components */}
        {<thead>
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => (
                <th {...column.getHeaderProps()}>
                  <Form.Label htmlFor={column.id} {...column.getSortByToggleProps()}>
                    {column.render("Header")}
                    {generateSortingIndicator(column)}
                  </Form.Label>
                  <Filter id={headerGroup.id} column={column} />
                </th>
              ))}
            </tr>
          ))}
        </thead>}
        <tbody {...getTableBodyProps()}>
          {page.map(row => {
            prepareRow(row)
            return (
              <tr {...row.getRowProps()} onClick={() => props.onClick(row.original.id)} style={{cursor: "pointer"}}>
                {row.cells.map(cell => {
                  return <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                })}
              </tr>
            )
          })}
        </tbody>
      </Table>
      <Row className="d-flex justify-content-between">
        <Col md={2}>
          <Button variant="outline-primary"
            onClick={() => gotoPage(0)}
            disabled={!canPreviousPage}>
            {"<<"}
          </Button>
          <Button
            variant="outline-primary"
            onClick={previousPage}
            disabled={!canPreviousPage}
          >
            {"<"}
          </Button>
        </Col>
        <Col md={2} style={{ marginTop: 7 }}>
          Page{" "}
          <strong>
            {currentPage} of {totalPages}
          </strong>
        </Col>
        <Col md={2}>
          <Input
            id="pgNumInput"
            type="number"
            min={totalPages === 0 ? 0 : 1}
            style={{ width: 70 }}
            max={totalPages}
            value={currentPage}
            disabled={totalPages === 0}
            onChange={onChangePageNumber}
          />
        </Col>
        <Col md={2}>
          <Input id="pgSizeInput" type="select" value={pageSize} onChange={onChangePageSize}>
            {[10, 20, 50, 100].map(pageSize => (
              <option key={pageSize} value={pageSize}>
                Show {pageSize}
              </option>
            ))}
          </Input>
        </Col>
        <Col md={2}>
          <Button variant="outline-primary" onClick={nextPage} disabled={!canNextPage}>
            {">"}
          </Button>
          <Button
            variant="outline-primary"
            onClick={() => gotoPage(pageCount - 1)}
            disabled={!canNextPage}
          >
            {">>"}
          </Button>
        </Col>
        <Col md={2}>
          <Button variant="outline-primary" className="ms-2" onClick={() => {exportData("csv", true);}}>
            Export To CSV
          </Button>
        </Col>
      </Row>
    </Fragment>
  )
}

TableContainer.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  onClick: PropTypes.func.isRequired,
  persistKey: PropTypes.string
}

export default TableContainer
